import Grid from "@mui/material/Grid";
import { CommentDetailProps } from "./commentDetail";
import { Toast as showToast } from "core/components/global/toast/toast";
import { useTranslation } from "react-i18next";
import { getIncidentDetailsByIdService } from "modules/flywheel/services/incidentService";
import { AuthContext } from "core/contexts/auth/auth";
import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";

import {
  IncidentSummaryContainer,
  IncidentDetailsContainer,
  CloseBtnContainer,
  IncidentSummaryHeader,
} from "./incidentSummary.styled";
import { CloseBtn } from "../sideModal.styled";

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AccordionSummary, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from "@mui/material";
import { Ioc } from "./iocRow";
import CrossBtnIcon from "assets/CrossBtnIcon";
import { useTheme } from "styled-components";
import { FlywheelTableSummary } from "./tableSections/main";
import { MalwareUploadTableSummary } from "./tableSections/awsmu";
import { UbaTableSummary } from "./tableSections/ubafu";
import { MalwareChatTableSummary } from "./tableSections/awsmc";
import { IrUploadTableSummary } from "./tableSections/awsfu";
import { IconMaximize, IconMinimize, IconX } from "@tabler/icons-react";
import { setPreviewWidth, setSelectedRow } from "modules/flywheel/store/incident";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";

export interface ShowMoreState {
  [key: string]: boolean;
}

export interface IRelatedIncidents {
  id: string;
  description?: string;
  createdDate?: string;
}

interface IncidentDetailsAccordionProps {
  assigned?: string[];
  tags?: any[];
  org?: string;
  lastModified: string;
  createdDate: string;
  code: string;
}

export function IncidentDetailsAccordion({assigned, tags, org, lastModified, createdDate, code}:IncidentDetailsAccordionProps) {
  return (
    <div>
      <IncidentDetailsContainer className="shadow-xl">
        <AccordionSummary
          component={Paper}
          elevation={1}
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Typography>Incident Details</Typography>
        </AccordionSummary>
        <TableContainer component={Paper} elevation={2}>
          <Table width={690} aria-label="incident-details">
            <TableBody>
                <TableRow
                  key={'IncidentDetailsAccordion0'}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">AWS Org</TableCell>
                  {
                    org ?
                    <TableCell component="th" scope="row">{org}</TableCell> : <TableCell></TableCell>
                  }
                </TableRow>
                <TableRow
                  key={'IncidentDetailsAccordion2'}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">Assigned User(s)</TableCell>
                  <TableCell component="th" scope="row">
                    {assigned?.map((name) => (
                      <span key={name}>
                        {name}
                      </span>
                    ))}
                  </TableCell>
                </TableRow>
                <TableRow
                  key={'IncidentDetailsAccordion3'}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">Last Modified</TableCell>
                  <TableCell component="th" scope="row">{lastModified}</TableCell>
                </TableRow>
                <TableRow
                  key={'IncidentDetailsAccordion4'}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">Created</TableCell>
                  <TableCell component="th" scope="row">{createdDate}</TableCell>
                </TableRow>
                { (code && code !== "") ? 
                    <TableRow
                      key={"IncidentDetailsAccordion5"}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">Code</TableCell>
                      <TableCell component="th" scope="row">
                        {code}
                      </TableCell>
                    </TableRow>
                  :
                    <></>
                }
            </TableBody>
          </Table>
        </TableContainer>
      </IncidentDetailsContainer>
    </div>
  );
}

export interface IncidentSummarySchema {
  id: string;
  severity: string;
  status: string;
  classification: string;
  organization?: string;
  assignedUsers?: string[];
  description: string;
  flywheelDetectionRule: string;
  flywheelReport: string;
  flywheelMitre: string;
  flywheelIocs: Ioc[];
  flywheelCve: string;
  createdDate: string;
  lastModified: string;
  comments: CommentDetailProps[];
  tags?: any[];
  relatedIncidents?: any[];
  employee_id?: string;
  ai_response_uba_risk_score?: string;
  case_information?: string;
  ai_response_risk_score?: string;
  event?: string;
  code_analysis?: string;
  event_summary?: string;
  file_analysis?: string;
  slim_output_answers?: string;
  completed_code_review?: string;
  code?: string;
}

export interface IncidentSummaryProps {
  setOpenSideModal: Dispatch<SetStateAction<boolean>>;
  selectedId: string;
  selectedType: string;
  showMore: ShowMoreState;
  handleClick: (string) => void;
}

export const IncidentSummary = ({
  setOpenSideModal,
  selectedId,
  selectedType,
  showMore,
  handleClick,
}: IncidentSummaryProps) => {
  const theme = useTheme();
  const { afUser: user, checkAuthToken } = useContext(AuthContext);
  const { t } = useTranslation();
  const [incidentSummary, setIncidentSummary] = useState<IncidentSummarySchema>();
  const [ isLoadingGeneral, setIsLoadingGeneral ] = useState(true);
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [prevWidth, setPrevWidth] = useState(50);
  const dispatch = useAppDispatch();
  const incidentState = useAppSelector((state) => state.incidents);
  /**
   * On component initiation, the data for this will be fetched
   */
  useEffect(() => {
    const fetchData = async (id) => {  
      const token = await checkAuthToken();
      const config = { 'Authorization': token, 'Content-Type': 'application/json' };

      getIncidentDetailsByIdService(config, selectedId, selectedType).then((response) => {
        if (response) {
          setIncidentSummary(response);
        } else {
          showToast(`Unable to fetch data on incident ${id}`, "error");
        }
        setIsLoadingGeneral(false);
      });
    };
  
    fetchData(selectedId);
  }, [])
  
  const [parsedDescription, setParsedDescription] = useState<[string, string | undefined][]>([
    [ "Who", "" ], 
    [ "What", "" ], 
    [ "When", "" ], 
    [ "Where", "" ], 
    [ "Why", "" ]
  ]);

  useEffect(() => {
    if (incidentSummary?.description && !isLoadingGeneral) {
      // Iterate through each line starting from index 1
      // sample = WHO: - XYZ WHAT: XYZ WHERE: XYZ WHEN: X:Y:Z
      const lines = incidentSummary?.description.split('\n');
      const keyValuePairs = {};

      // Iterate over the lines and extract key-value pairs
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        
        // Match the key-value pattern like "• WHO: some value"
        const match = line.match(/^• (\w+): (.+)$/);
        
        if (match) {
          const key = match[1].toUpperCase();  // Get the key (WHO, WHAT, etc.)
          const value = match[2];  // Get the value (the description after the colon)
          
          keyValuePairs[key] = value;
        }
      }

      // Display the final key-value pairs object
      setParsedDescription(Object.entries(keyValuePairs));
    }
  }, [isLoadingGeneral, incidentSummary?.description])

  const [statusColor, setStatusColor] = useState(Object());

  const statusMapping = {
    "Open": {
      "bk": theme.colors.open_incident,
      "text": theme.colors.textColor
    },
    "In Progress": {
      "bk": theme.colors.in_progress_incident,
      "text": theme.colors.dark
    },
    "Closed": {
      "bk": theme.colors.closed_incident,
      "text": theme.colors.dark
    },
    "Request Tuning": {
      "bk": theme.colors.requested_incident,
      "text": theme.colors.dark
    }
  }

  useEffect(() => {
    if (incidentSummary?.status && statusMapping[incidentSummary?.status]) {
      setStatusColor({
        text: statusMapping[incidentSummary?.status].text,
        bk: statusMapping[incidentSummary?.status].bk
      });
    }
  }, [incidentSummary?.status]);
 const toggleFullWidth = ({close}: {close?: boolean} = {}) => {
    try {
      if (close) {
        // Close the side panel and reset selected row
        setOpenSideModal(false);
        dispatch(setSelectedRow({ id: "", type: "" }));
      } else {
        if (isFullWidth) {
          // Return to previous width using Redux
          if (prevWidth > 10) {
            dispatch(setPreviewWidth(prevWidth));
          } else {
            dispatch(setPreviewWidth(30));
          }
        } else {
          // Save current width before going full width
          setPrevWidth(incidentState.previewWidth);
          
          // Set to maximum width using Redux
          dispatch(setPreviewWidth(100));
        }
        setIsFullWidth(!isFullWidth);
      }
    } catch (error) {
      console.error("Error in toggleFullWidth:", error);
    }
  };

  return (
    <IncidentSummaryContainer>
      <IncidentSummaryHeader>
        {/* First Column: 80%: flex 8 */}
        <span>
          <h5 className="text-break">{t("INCIDENTS_PAGE.INCIDENT")}: {!isLoadingGeneral && incidentSummary?.id}</h5>
          {isLoadingGeneral && <Skeleton className="ms-2" style={{ width: '55%', height: 36 }} animation="wave" variant="text" />}
        </span>

        {/* Second Column: 20%: flex 2 */}
        <span>
          <button
            onClick={() => toggleFullWidth()}
            style={{ color: theme.colors.textColor}}
            className={`p-2 rounded-full transition-colors`}
            title="Toggle width"
          >
            {isFullWidth ? (
              <IconMinimize size={18} stroke={1.5} />
            ) : (
              <IconMaximize size={18} stroke={1.5} />
            )}
          </button>
          <button
            onClick={() => toggleFullWidth({close: true})}
            style={{ color: theme.colors.textColor}}
            className={`p-2 rounded-full transition-colors`}
            title="Close"
          >
            <IconX size={18} stroke={1.5} />
          </button>
        </span>
      </IncidentSummaryHeader>

      { isLoadingGeneral ? <>
          <div className="d-flex flex-row">
              <Skeleton  style={{width: 150, height: 52}} animation="wave" variant="text" />
              <Skeleton  className="ms-2" style={{width: 150, height: 52}} animation="wave" variant="text" />
          </div>
          </> : <>
          <div className="d-flex flex-row">
            { incidentSummary ?
              <>
                <div style={{backgroundColor: statusColor ? statusColor.bk : "#7b7881"}} className="mt-2 mb-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow">
                  <strong style={{color: statusColor ? statusColor.text : "#fff"}}>{incidentSummary?.status}</strong>
                </div>
                <div style={{backgroundColor: theme.severity.range[incidentSummary ? incidentSummary.severity : ""]}} className="m-2 ms-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow">
                  <strong style={{color: '#273746'}}>{t("INCIDENTS_PAGE.SEVERITY")}: {incidentSummary?.severity}</strong>
                </div>
              </> :
              <>
                <div style={{backgroundColor: "#7b7881"}} className="mt-2 mb-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow">
                  <strong style={{color: statusColor ? statusColor.text : "#fff"}}>Status</strong>
                </div>
                <div style={{backgroundColor: "#7b7881"}} className="m-2 ms-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow">
                  <strong style={{color: "#fff"}}>{t("INCIDENTS_PAGE.SEVERITY")}</strong>
                </div>
              </>
            }
          </div>
        </>
      }

      <IncidentDetailsAccordion code={incidentSummary?.code || ''} assigned={incidentSummary?.assignedUsers} tags={incidentSummary?.tags} org={incidentSummary?.organization} lastModified={incidentSummary?.lastModified || ""} createdDate={incidentSummary?.createdDate || ""}/>
      
      {
        selectedType === "main" ?
          <>
            <span className="d-flex flex-column">
              <Grid item>
                <TableContainer >
                  <Table aria-label="alert-summary">
                    <TableBody>
                      { 
                        parsedDescription.map(([key, value], index) => (
                          <TableRow key={`description-${index}`}>
                            <TableCell component="th" scope="row" className="d-flex flex-column ps-0 pe-0">
                              <span style={{color: theme.colors.textDisabledColor}}>{key.slice(0,1) + key.slice(1,).toLowerCase()}</span>
                              {
                                !isLoadingGeneral ? <>
                                  <p className="pt-1" style={{color: theme.colors.textColor, whiteSpace: 'pre-line'}}>{value}</p>
                                  </>: <>
                                  <Skeleton  style={{width: '65%', height: 36}} animation="wave" variant="text" />
                                </>
                              }
                            </TableCell>
                          </TableRow>
                        ))
                      }
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </span>
            <FlywheelTableSummary incidentSummary={incidentSummary} showMore={showMore} handleClick={handleClick} user={user}/>
          </>
        : selectedType === "awsmc" ?
        <MalwareChatTableSummary incidentSummary={incidentSummary} user={user}/>
        : selectedType === "awsmu" ?
        <MalwareUploadTableSummary incidentSummary={incidentSummary} user={user}/>
        : selectedType === "ubafu" ?
        <UbaTableSummary incidentSummary={incidentSummary} user={user}/>
        : selectedType === "awsfu" ?
        <IrUploadTableSummary incidentSummary={incidentSummary} user={user}/>
        : <></>
      }

    </IncidentSummaryContainer>
  );
};

export default IncidentSummary;
